import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Modal,
  Form,
  Alert,
  Spinner,
  InputGroup,
  Dropdown,
  Pagination,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  IconPlus,
  IconCopy,
  IconEdit,
  IconTrash,
  IconRefresh,
  IconToggleLeft,
  IconToggleRight,
  IconCalendarTime,
  IconUsers,
  IconLink,
  IconSearch,
  IconDots,
  IconCheck,
  IconX,
  IconCopyCheck,
} from "@tabler/icons-react";
import { useAgentHubContext } from "../contexts/AgentHubContext";
import NotificationsContext from "../../../contexts/NotificationsContext";
import {
  getPublicLinks,
  createPublicLink,
  updatePublicLink,
  deletePublicLink,
  togglePublicLinkStatus,
  regeneratePublicLinkToken,
  // duplicatePublicLink,
  formatPublicLinkUrl,
  calculateTimeRemaining,
  getLinkStatus,
} from "../../../services/WaPublicLinkServices";

// Esquema de validação com Yup
const linkSchema = yup.object().shape({
  isActive: yup.boolean(),
});

const WaPublicLink = () => {
  const { agentContext } = useAgentHubContext();
  const { agentId } = useParams();
  const { setNotify } = useContext(NotificationsContext);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [copySuccess, setCopySuccess] = useState({});

  // Estados de paginação e filtros
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // React Hook Form
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
    setValue,
  } = useForm({
    resolver: yupResolver(linkSchema),
    defaultValues: {
      isActive: true,
    },
  });

  // Carregar links do agente
  const loadLinks = async () => {
    if (!agentId) return;

    setLoading(true);

    try {
      const result = await getPublicLinks({
        page: currentPage,
        pageSize,
        search: searchTerm,
        status: statusFilter !== "all" ? statusFilter : undefined,
      });

      if (result.success) {
        setLinks(result.data.results || []);
        setTotalPages(Math.ceil((result.data.count || 0) / pageSize));
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro ao carregar tokens",
          text: result.error || "Erro ao carregar tokens públicos",
          confirmButtonColor: "var(--primary)",
        });
      }
    } catch (err) {
      console.error("Erro ao carregar links:", err);
      Swal.fire({
        icon: "error",
        title: "Erro inesperado",
        text: "Erro inesperado ao carregar tokens públicos",
        confirmButtonColor: "var(--primary)",
      });
    } finally {
      setLoading(false);
    }
  };

  // Carregar links quando componente monta ou filtros mudam
  useEffect(() => {
    loadLinks();
  }, [agentId, currentPage, searchTerm, statusFilter]);

  // Função para abrir modal de criar/editar
  const openModal = (link = null) => {
    setEditingLink(link);

    if (link) {
      // Preencher formulário para edição
      setValue("isActive", link.is_active);
    } else {
      // Limpar formulário para criação
      reset();
    }

    setShowModal(true);
  };

  // Função para fechar modal
  const closeModal = () => {
    setShowModal(false);
    setEditingLink(null);
    reset();
  };

  // Função para salvar link (criar ou atualizar)
  const onSubmit = async (data) => {
    if (!agentId) return;

    try {
      let result;

      if (editingLink) {
        // Atualizar link existente
        result = await updatePublicLink(editingLink.id, data);
      } else {
        // Criar novo link
        result = await createPublicLink({ agent: parseInt(agentId) });
      }

      if (result.success) {
        closeModal();
        loadLinks(); // Recarregar lista
        
        setNotify({
          type: "success",
          title: editingLink ? "Token atualizado!" : "Token criado!",
          message: editingLink 
            ? "Token público atualizado com sucesso" 
            : `Novo token criado: ${result.data.token}`,
          visible: true,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro ao salvar token",
          text: result.error || "Erro ao salvar token público",
          confirmButtonColor: "var(--primary)",
        });
      }
    } catch (err) {
      console.error("Erro ao salvar link:", err);
      Swal.fire({
        icon: "error",
        title: "Erro inesperado",
        text: "Erro inesperado ao salvar token público",
        confirmButtonColor: "var(--primary)",
      });
    }
  };

  // Função para alternar status do link
  const handleToggleStatus = async (linkId, currentStatus) => {
    setActionLoading((prev) => ({ ...prev, [`toggle_${linkId}`]: true }));

    try {
      const result = await togglePublicLinkStatus(linkId);

      if (result.success) {
        loadLinks(); // Recarregar lista
        
        setNotify({
          type: "success",
          title: "Status alterado!",
          message: `Token ${currentStatus ? "desativado" : "ativado"} com sucesso`,
          visible: true,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro ao alterar status",
          text: result.error || "Erro ao alterar status do token",
          confirmButtonColor: "var(--primary)",
        });
      }
    } catch (err) {
      console.error("Erro ao alterar status:", err);
      Swal.fire({
        icon: "error",
        title: "Erro inesperado",
        text: "Erro inesperado ao alterar status do token",
        confirmButtonColor: "var(--primary)",
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, [`toggle_${linkId}`]: false }));
    }
  };

  // Função para excluir link
  const handleDeleteLink = async (linkId, linkName) => {
    const result = await Swal.fire({
      title: "Excluir token?",
      html: `Tem certeza que deseja excluir o token do agente <strong>"${linkName}"</strong>?<br><br>Esta ação não pode ser desfeita.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    setActionLoading((prev) => ({ ...prev, [`delete_${linkId}`]: true }));

    try {
      const deleteResult = await deletePublicLink(linkId);

      if (deleteResult.success) {
        loadLinks(); // Recarregar lista
        
        setNotify({
          type: "success",
          title: "Token excluído!",
          message: "Token público excluído com sucesso",
          visible: true,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro ao excluir token",
          text: deleteResult.error || "Erro ao excluir token público",
          confirmButtonColor: "var(--primary)",
        });
      }
    } catch (err) {
      console.error("Erro ao excluir link:", err);
      Swal.fire({
        icon: "error",
        title: "Erro inesperado",
        text: "Erro inesperado ao excluir token público",
        confirmButtonColor: "var(--primary)",
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, [`delete_${linkId}`]: false }));
    }
  };

  // Função para regenerar token
  const handleRegenerateToken = async (linkId, linkName) => {
    const result = await Swal.fire({
      title: "Regenerar token?",
      html: `Tem certeza que deseja regenerar o token do agente <strong>"${linkName}"</strong>?<br><br>O token atual ficará inválido.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--primary)",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sim, regenerar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    setActionLoading((prev) => ({ ...prev, [`regenerate_${linkId}`]: true }));

    try {
      const regenerateResult = await regeneratePublicLinkToken(linkId);

      if (regenerateResult.success) {
        loadLinks(); // Recarregar lista
        
        setNotify({
          type: "success",
          title: "Token regenerado!",
          message: `Novo token: ${regenerateResult.data.new_token?.token || "Token regenerado com sucesso"}`,
          visible: true,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro ao regenerar token",
          text: regenerateResult.error || "Erro ao regenerar token público",
          confirmButtonColor: "var(--primary)",
        });
      }
    } catch (err) {
      console.error("Erro ao regenerar token:", err);
      Swal.fire({
        icon: "error",
        title: "Erro inesperado",
        text: "Erro inesperado ao regenerar token público",
        confirmButtonColor: "var(--primary)",
      });
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [`regenerate_${linkId}`]: false,
      }));
    }
  };

  // Função para duplicar link
  const handleDuplicateLink = async (linkId) => {
    setActionLoading((prev) => ({ ...prev, [`duplicate_${linkId}`]: true }));

    // try {
    //   const result = await duplicatePublicLink(agentContext.id, linkId);

    //   if (result.success) {
    //     loadLinks(); // Recarregar lista
    //   } else {
    //     setError(result.error || "Erro ao duplicar link");
    //   }
    // } catch (err) {
    //   setError("Erro inesperado ao duplicar link");
    //   console.error("Erro ao duplicar link:", err);
    // } finally {
    //   setActionLoading(prev => ({ ...prev, [`duplicate_${linkId}`]: false }));
    // }
  };

  // Função para copiar link
  const handleCopyLink = async (token, linkId) => {
    const url = formatPublicLinkUrl(token);

    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess((prev) => ({ ...prev, [linkId]: true }));

      setNotify({
        type: "success",
        title: "Link copiado!",
        message: "Link do token foi copiado para a área de transferência",
        visible: true,
      });

      // Limpar indicador de sucesso após 2 segundos
      setTimeout(() => {
        setCopySuccess((prev) => ({ ...prev, [linkId]: false }));
      }, 2000);
    } catch (err) {
      console.error("Erro ao copiar link:", err);
      // Fallback para browsers antigos
      try {
        const textArea = document.createElement("textarea");
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);

        setCopySuccess((prev) => ({ ...prev, [linkId]: true }));
        
        setNotify({
          type: "success",
          title: "Link copiado!",
          message: "Link do token foi copiado para a área de transferência",
          visible: true,
        });

        setTimeout(() => {
          setCopySuccess((prev) => ({ ...prev, [linkId]: false }));
        }, 2000);
      } catch (fallbackErr) {
        Swal.fire({
          icon: "error",
          title: "Erro ao copiar",
          text: "Não foi possível copiar o link automaticamente",
          confirmButtonColor: "var(--primary)",
        });
      }
    }
  };

  // Renderizar status badge
  const renderStatusBadge = (link) => {
    const status = getLinkStatus(link);
    return (
      <Badge
        bg={status.color === "secondary" ? "secondary" : status.color}
        className="d-flex align-items-center gap-1"
      >
        {status.status === "active" && <IconCheck size={12} />}
        {status.status === "inactive" && <IconX size={12} />}
        {status.status === "expired" && <IconCalendarTime size={12} />}
        {status.status === "used" && <IconUsers size={12} />}
        {status.label}
      </Badge>
    );
  };

  // Renderizar ações da linha
  const renderRowActions = (link) => (
    <Dropdown>
      <Dropdown.Toggle
        variant="outline-secondary"
        size="sm"
        id={`dropdown-${link.id}`}
        className="border-0 dropdown-toggle-no-arrow"
      >
        <IconDots size={16} />
      </Dropdown.Toggle>

      {createPortal(
        <Dropdown.Menu className="dropdown-menu-portal">
          <Dropdown.Item
            onClick={() => handleCopyLink(link.token, link.id)}
            className="d-flex align-items-center gap-2"
          >
            {copySuccess[link.id] ? (
              <IconCopyCheck size={16} />
            ) : (
              <IconCopy size={16} />
            )}
            {copySuccess[link.id] ? "Copiado!" : "Copiar Link"}
          </Dropdown.Item>

          <Dropdown.Item
            onClick={() => openModal(link)}
            className="d-flex align-items-center gap-2"
          >
            <IconEdit size={16} />
            Editar
          </Dropdown.Item>

          <Dropdown.Item
            onClick={() => handleToggleStatus(link.id, link.is_active)}
            className="d-flex align-items-center gap-2"
            disabled={actionLoading[`toggle_${link.id}`]}
          >
            {link.is_active ? (
              <IconToggleLeft size={16} />
            ) : (
              <IconToggleRight size={16} />
            )}
            {link.is_active ? "Desativar" : "Ativar"}
          </Dropdown.Item>

          <Dropdown.Divider />


          <Dropdown.Item
            onClick={() => handleRegenerateToken(link.id, link.agent_name)}
            className="d-flex align-items-center gap-2"
            disabled={actionLoading[`regenerate_${link.id}`]}
          >
            <IconRefresh size={16} />
            Regenerar Token
          </Dropdown.Item>

          <Dropdown.Divider />

          <Dropdown.Item
            onClick={() => handleDeleteLink(link.id, link.agent_name)}
            className="d-flex align-items-center gap-2 text-danger"
            disabled={actionLoading[`delete_${link.id}`]}
          >
            <IconTrash size={16} />
            Excluir
          </Dropdown.Item>
        </Dropdown.Menu>,
        document.body
      )}
    </Dropdown>
  );

  if (!agentId) {
    return (
      <Container className="py-4">
        <Alert variant="warning">
          <IconX className="me-2" />
          Agente não encontrado. Volte para a lista de agentes e tente
          novamente.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="fw-bold text-dark mb-1">
                <IconLink className="me-2" />
                Links Públicos WhatsApp
              </h2>
              <p className="text-muted mb-0">
                Gerencie links para conexão pública de WhatsApp do agente "
                {agentContext.agent_name}"
              </p>
            </div>
            <Button
              variant="primary"
              onClick={() => openModal()}
              className="d-flex align-items-center gap-2"
              style={{
                background: "var(--gradient-primary)",
                border: "none",
                borderRadius: "var(--border-radius-md)",
                padding: "var(--spacing-sm) var(--spacing-md)",
              }}
            >
              <IconPlus size={18} />
              Novo Link
            </Button>
          </div>
        </Col>
      </Row>

      {/* Filtros e Busca */}
      <Row className="mb-4">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>
              <IconSearch size={16} />
            </InputGroup.Text>
            <Form.Control
              placeholder="Buscar links..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={3}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Todos os status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
            <option value="expired">Expirados</option>
            <option value="used">Usados</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Button
            variant="outline-secondary"
            onClick={loadLinks}
            disabled={loading}
            className="d-flex align-items-center gap-2"
          >
            <IconRefresh size={16} className={loading ? "spinning" : ""} />
            Atualizar
          </Button>
        </Col>
      </Row>


      {/* Tabela de Links */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-2">Carregando links...</p>
            </div>
          ) : links.length === 0 ? (
            <div className="text-center py-5">
              <IconLink size={48} className="text-muted mb-3" />
              <h4 className="text-muted">Nenhum link encontrado</h4>
              <p className="text-muted">
                {searchTerm || statusFilter !== "all"
                  ? "Tente ajustar os filtros de busca"
                  : "Crie seu primeiro link público para conexão WhatsApp"}
              </p>
              {!searchTerm && statusFilter === "all" && (
                <Button
                  variant="primary"
                  onClick={() => openModal()}
                  className="mt-3"
                >
                  <IconPlus className="me-2" />
                  Criar Primeiro Link
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table hover responsive className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Agente</th>
                    <th>Status</th>
                    <th>Token</th>
                    <th>Expira em</th>
                    <th>Criado</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {links.map((link) => {
                    const timeRemaining = calculateTimeRemaining(
                      link.expires_at
                    );
                    const createdDate = new Date(
                      link.created_at
                    ).toLocaleDateString("pt-BR");

                    return (
                      <tr key={link.id}>
                        <td>
                          <div>
                            <div className="fw-medium">{link.agent_name}</div>
                            <small className="text-muted">ID: {link.agent_id}</small>
                          </div>
                        </td>
                        <td>{renderStatusBadge(link)}</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded">
                            {link.token}
                          </code>
                        </td>
                        <td>
                          <span
                            className={
                              timeRemaining.expired
                                ? "text-danger"
                                : "text-muted"
                            }
                          >
                            {timeRemaining.formatted}
                          </span>
                        </td>
                        <td className="text-muted">{createdDate}</td>
                        <td className="text-center">
                          {renderRowActions(link)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center p-3">
                  <Pagination>
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    />
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page =
                        Math.max(1, Math.min(totalPages - 4, currentPage - 2)) +
                        i;
                      return (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal de Criar/Editar Link */}
      <Modal show={showModal} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingLink ? "Editar Link Público" : "Novo Link Público"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            {editingLink ? (
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-0">
                    <Form.Check
                      type="switch"
                      label="Token ativo"
                      {...register("isActive")}
                    />
                    <Form.Text className="text-muted">
                      Tokens inativos não podem ser usados para conexão
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            ) : (
              <Alert variant="info">
                <p className="mb-0">
                  Um novo token de 6 dígitos será gerado automaticamente para o agente{" "}
                  <strong>{agentContext?.agent_name}</strong>.
                </p>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting && (
                <Spinner animation="border" size="sm" className="me-2" />
              )}
              {editingLink ? "Atualizar Link" : "Criar Link"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Estilos CSS customizados */}
      <style jsx>{`
        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .progress {
          border-radius: var(--border-radius-sm);
          overflow: hidden;
        }

        .progress-bar {
          transition: width 0.3s ease;
        }

        .table th {
          font-weight: 600;
          border-bottom: 2px solid var(--gray-200);
        }

        .table td {
          vertical-align: middle;
          border-bottom: 1px solid var(--gray-100);
        }

        .table tbody tr:hover {
          background-color: var(--gray-50);
        }

        /* Remove a seta do dropdown toggle */
        .dropdown-toggle-no-arrow::after {
          display: none !important;
        }

        /* Força o dropdown menu a aparecer acima de outros elementos */
        .dropdown-menu-portal {
          z-index: 9999 !important;
          position: fixed !important;
        }

        /* Força a tabela a não esconder o dropdown */
        .table-responsive {
          overflow: visible !important;
        }

        .card-body {
          overflow: visible !important;
        }
      `}</style>
      
      {/* Estilos CSS globais para dropdown */}
      <style>{`
        .dropdown-menu-portal {
          z-index: 9999 !important;
          position: absolute !important;
        }
        
        .table-responsive {
          overflow: visible !important;
        }
        
        .card-body {
          overflow: visible !important;
        }
      `}</style>
    </Container>
  );
};

export default WaPublicLink;
